<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SessionPreferenceFields from './session/SessionPreferenceFields.vue';

const props = defineProps({
  descriptor: { type: Object, required: true },
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'convert'].includes(value),
  },
  inbox: { type: Object, default: null },
});

// Converting keeps the inbox, its number and its conversations: only the provider changes,
// and the instance is created (or, from the legacy engine of this provider, kept) for it.
const isConvertMode = computed(() => props.mode === 'convert');

const router = useRouter();
const store = useStore();
const { t } = useI18n();
const inboxName = ref(isConvertMode.value ? props.inbox?.name || '' : '');
const phoneNumber = ref(
  isConvertMode.value ? props.inbox?.phone_number || '' : ''
);
// The provider creates the instance, so the form asks only for the preferences it is
// created with: the same ones as the external instance, seeded from the catalog.
const fieldValues = ref({});
watch(
  () => props.descriptor,
  value => {
    fieldValues.value = Object.fromEntries(
      (value?.fields ?? []).map(field => [
        field.name,
        field.default ?? (field.type === 'boolean' ? false : ''),
      ])
    );
  },
  { immediate: true }
);
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);
const rules = computed(() => ({
  inboxName: { required },
  phoneNumber: { required, isPhoneE164OrEmpty },
}));
const v$ = useVuelidate(rules, { inboxName, phoneNumber });

const convertChannel = async () => {
  try {
    await store.dispatch('inboxes/convertProvider', {
      inboxId: props.inbox.id,
      provider: props.descriptor.provider,
      providerConfig: { ...fieldValues.value },
      whatsappProviderId: props.descriptor.provider_id,
    });
    useAlert(t('INBOX_MGMT.CONVERT.API.SUCCESS_MESSAGE'));
    // The finish page opens the QR on its own for a managed inbox.
    router.replace({
      name: 'settings_inbox_finish',
      params: { inbox_id: props.inbox.id },
    });
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.CONVERT.API.ERROR_MESSAGE'));
  }
};

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (isConvertMode.value) {
    await convertChannel();
    return;
  }

  try {
    const channel = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value,
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumber.value,
        provider: props.descriptor.provider,
        whatsapp_provider_id: props.descriptor.provider_id,
        provider_config: { ...fieldValues.value },
      },
    });
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <form class="flex flex-wrap mx-0" @submit.prevent="createChannel">
    <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :disabled="isConvertMode"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>
    <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :disabled="isConvertMode"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>
    <SessionPreferenceFields
      v-model="fieldValues"
      :fields="descriptor.fields ?? []"
    />
    <p class="w-[65%] mt-2 text-sm text-n-slate-11">
      {{
        isConvertMode
          ? $t('INBOX_MGMT.ADD.WHATSAPP.MANAGED.CONVERT_HINT')
          : $t('INBOX_MGMT.ADD.WHATSAPP.MANAGED.HINT')
      }}
    </p>
    <div class="w-full mt-5">
      <NextButton
        :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
        type="submit"
        solid
        blue
        :label="
          isConvertMode
            ? $t('INBOX_MGMT.CONVERT.SUBMIT_BUTTON')
            : $t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')
        "
      />
    </div>
  </form>
</template>
